import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { maintenanceAPI, propertyAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Wrench,
  AlertTriangle,
  Clock,
  CheckCircle,
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  Crown,
  User,
  Filter,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const Maintenance = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await maintenanceAPI.getAll();
      setTickets(res.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await maintenanceAPI.updateStatus(ticketId, status);
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSendMessage = async (ticketId) => {
    if (!newMessage.trim()) return;
    try {
      await maintenanceAPI.addMessage(ticketId, newMessage.trim());
      setNewMessage('');
      loadData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OPEN': return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'IN_PROGRESS': return <Clock className="w-5 h-5 text-blue-400" />;
      case 'RESOLVED': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'CLOSED': return <CheckCircle className="w-5 h-5 text-gray-400" />;
      default: return <Wrench className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'IN_PROGRESS': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'RESOLVED': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'CLOSED': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'text-red-400';
      case 'HIGH': return 'text-orange-400';
      case 'MEDIUM': return 'text-amber-400';
      case 'LOW': return 'text-emerald-400';
      default: return 'text-gray-400';
    }
  };

  const filteredTickets = statusFilter === 'all' ? tickets : tickets.filter(t => t.status === statusFilter);

  const renderThread = (ticket) => {
    const messages = ticket.messages || [];
    const notes = ticket.notes || [];
    const all = [
      ...messages.map(m => ({ ...m, type: 'message' })),
      ...notes.map(n => ({ ...n, type: 'note' })),
    ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    return (
      <div className="mt-4 pt-4 border-t border-border space-y-3" data-testid={`ticket-thread-${ticket.id}`}>
        <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Conversation ({all.length})
        </p>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {all.map((entry) => {
            const isMe = entry.author_id === user?.id;
            return (
              <div key={entry.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 ${isMe ? 'bg-gold/10 border border-gold/20' : 'bg-white border border-[#e5e7eb]'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {entry.author_role === 'LANDLORD' || entry.author_role === 'SUPER_ADMIN' ? (
                      <Crown className="w-3 h-3 text-gold" />
                    ) : (
                      <User className="w-3 h-3 text-blue-400" />
                    )}
                    <span className="text-xs font-medium">{entry.author_name}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(entry.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm">{entry.text}</p>
                </div>
              </div>
            );
          })}
          {all.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No messages yet</p>}
        </div>
        {ticket.status !== 'CLOSED' && (
          <div className="flex gap-2">
            <Input
              value={expandedTicket === ticket.id ? newMessage : ''}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Reply to tenant..."
              className="bg-white border-[#e5e7eb] text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(ticket.id)}
              data-testid={`message-input-${ticket.id}`}
            />
            <Button
              size="icon"
              className="bg-gold text-white hover:bg-gold-light shrink-0"
              onClick={() => handleSendMessage(ticket.id)}
              data-testid={`send-message-${ticket.id}`}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('maintenance.title')}>
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-card animate-pulse" />)}
        </div>
      </DashboardLayout>
    );
  }

  const counts = {
    all: tickets.length,
    OPEN: tickets.filter(t => t.status === 'OPEN').length,
    IN_PROGRESS: tickets.filter(t => t.status === 'IN_PROGRESS').length,
    RESOLVED: tickets.filter(t => t.status === 'RESOLVED').length,
    CLOSED: tickets.filter(t => t.status === 'CLOSED').length,
  };

  return (
    <DashboardLayout type="landlord" title={t('maintenance.title')}>
      <div className="space-y-6" data-testid="landlord-maintenance-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-muted-foreground">Manage maintenance requests from your tenants</p>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] h-9 bg-card border-border text-sm" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({counts.all})</SelectItem>
                <SelectItem value="OPEN">Open ({counts.OPEN})</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress ({counts.IN_PROGRESS})</SelectItem>
                <SelectItem value="RESOLVED">Resolved ({counts.RESOLVED})</SelectItem>
                <SelectItem value="CLOSED">Closed ({counts.CLOSED})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Open', count: counts.OPEN, color: 'text-amber-400', bg: 'bg-amber-500/10' },
            { label: 'In Progress', count: counts.IN_PROGRESS, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { label: 'Resolved', count: counts.RESOLVED, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'Closed', count: counts.CLOSED, color: 'text-gray-400', bg: 'bg-gray-500/10' },
          ].map(s => (
            <Card key={s.label} className="bg-card border-border">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
                  <p className={`text-2xl font-semibold ${s.color} mt-1`}>{s.count}</p>
                </div>
                <div className={`w-8 h-8 ${s.bg} flex items-center justify-center`}>
                  <Wrench className={`w-4 h-4 ${s.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tickets */}
        {filteredTickets.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Wrench className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">No Tickets Found</h3>
              <p className="text-muted-foreground">
                {statusFilter === 'all' ? 'No maintenance requests yet' : `No ${statusFilter.replace('_', ' ').toLowerCase()} tickets`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredTickets.map((ticket, index) => (
              <Card
                key={ticket.id}
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`ticket-card-${ticket.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {getStatusIcon(ticket.status)}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-medium">{ticket.title}</h3>
                          <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(ticket.status)}`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                          <span className={`text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                        </div>
                        <button
                          onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                          className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                          data-testid={`toggle-thread-${ticket.id}`}
                        >
                          {expandedTicket === ticket.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>

                      <p className="text-sm text-muted-foreground mb-3">{ticket.description}</p>

                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="text-sm text-muted-foreground">
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                          className="flex items-center gap-1 text-sm text-gold hover:underline"
                          data-testid={`view-thread-${ticket.id}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                          {(ticket.messages || []).length + (ticket.notes || []).length} messages
                        </button>

                        {/* Status change */}
                        {ticket.status !== 'CLOSED' && (
                          <div className="ml-auto flex gap-2">
                            {ticket.status === 'OPEN' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/10" onClick={() => handleStatusChange(ticket.id, 'IN_PROGRESS')} data-testid={`mark-in-progress-${ticket.id}`}>
                                Mark In Progress
                              </Button>
                            )}
                            {(ticket.status === 'IN_PROGRESS' || ticket.status === 'OPEN') && (
                              <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" onClick={() => handleStatusChange(ticket.id, 'RESOLVED')} data-testid={`mark-resolved-${ticket.id}`}>
                                Mark Resolved
                              </Button>
                            )}
                            {ticket.status === 'RESOLVED' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs border-gray-500/30 text-gray-400 hover:bg-gray-500/10" onClick={() => handleStatusChange(ticket.id, 'CLOSED')} data-testid={`mark-closed-${ticket.id}`}>
                                Close Ticket
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      {expandedTicket === ticket.id && renderThread(ticket)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Maintenance;
